import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { LevelUpModal } from '../components/LevelUpModal';
import { BadgeUnlockModal } from '../components/BadgeUnlockModal';

const API = `${''}/api`;
const GamificationContext = createContext(null);

export const GamificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [levelUpGrade, setLevelUpGrade] = useState(null);
  const [badgeQueue, setBadgeQueue] = useState([]);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/progress/me`);
      setProgress(res.data);
    } catch (e) {
      /* silencieux */
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Appelé après une action récompensée (note, feedback…) — toast + maj + célébrations.
  const notifyResult = useCallback((result) => {
    if (!result) return;
    if (result.awarded > 0) {
      toast.success(`+${result.awarded} points de contribution`, {
        description: 'Merci d’aider à améliorer Hippocrate MG !',
      });
    }
    if (result.streak_bonus > 0) {
      toast.success(`🔥 Série de ${result.streak_current} semaines · +${result.streak_bonus} pts`, {
        description: 'Votre régularité est récompensée !',
      });
    }
    setProgress((prev) => ({
      ...(prev || {}),
      xp: result.xp,
      grade: result.grade,
      next_grade: result.next_grade,
      progress_pct: result.progress_pct,
      xp_to_next: result.xp_to_next,
      is_max: result.is_max,
      streak_current: result.streak_current ?? prev?.streak_current,
      streak_best: result.streak_best ?? prev?.streak_best,
    }));
    if (result.leveled_up && result.grade) {
      setLevelUpGrade(result.grade);
    }
    const newBadges = result.newly_unlocked_badges || [];
    if (newBadges.length > 0) {
      setBadgeQueue((q) => [...q, ...newBadges]);
      // resynchronise la liste complète des badges de la page Progression
      refresh();
    }
  }, [refresh]);

  return (
    <GamificationContext.Provider value={{ progress, refresh, notifyResult }}>
      {children}
      <LevelUpModal grade={levelUpGrade} onClose={() => setLevelUpGrade(null)} />
      <BadgeUnlockModal badge={badgeQueue[0] || null} onClose={() => setBadgeQueue((q) => q.slice(1))} />
    </GamificationContext.Provider>
  );
};

export const useGamification = () => useContext(GamificationContext) || {};
